/**
 * VoiceInput — mic button with animated waveform and live transcription.
 * Uses browser-native SpeechRecognition API via useVoiceRecognition hook.
 */
import { Mic, Square } from "lucide-react";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { useTeeAgentStore } from "@/zustand/useTeeAgentStore";
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  className?: string;
}

export const VoiceInput = ({ onTranscript, className }: VoiceInputProps) => {
  const { setAgentState } = useTeeAgentStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const {
    isSupported,
    isRecording,
    interimTranscript,
    toggleRecording,
  } = useVoiceRecognition({
    onResult: (text) => {
      onTranscript(text);
      setAgentState("thinking");
    },
    onInterim: () => {
      // interim updates handled via interimTranscript state
    },
  });

  // Update agent state when recording starts/stops
  useEffect(() => {
    setAgentState(isRecording ? "listening" : "seeing");
  }, [isRecording, setAgentState]);

  // Waveform visualization using AudioContext
  useEffect(() => {
    if (!isRecording) {
      cancelAnimationFrame(animFrameRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      analyserRef.current = null;
      // Clear canvas
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    let cancelled = false;

    const startVisualizer = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 64;
        source.connect(analyser);
        analyserRef.current = analyser;

        const draw = () => {
          if (cancelled) return;
          const canvas = canvasRef.current;
          if (!canvas) return;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;

          const data = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(data);

          ctx.clearRect(0, 0, canvas.width, canvas.height);

          const barWidth = 3;
          const gap = 2;
          const bars = Math.floor(canvas.width / (barWidth + gap));
          const centerY = canvas.height / 2;

          for (let i = 0; i < bars && i < data.length; i++) {
            const val = data[i] / 255;
            const barHeight = Math.max(4, val * canvas.height * 0.8);
            const x = i * (barWidth + gap);

            const gradient = ctx.createLinearGradient(
              x, centerY - barHeight / 2,
              x, centerY + barHeight / 2
            );
            gradient.addColorStop(0, "#57CC99");
            gradient.addColorStop(1, "#06832B");

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.roundRect(x, centerY - barHeight / 2, barWidth, barHeight, 1.5);
            ctx.fill();
          }

          animFrameRef.current = requestAnimationFrame(draw);
        };

        draw();
      } catch {
        // Microphone access denied — waveform won't show but voice still works
      }
    };

    startVisualizer();

    return () => {
      cancelled = true;
      cancelAnimationFrame(animFrameRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [isRecording]);

  if (!isSupported) {
    return null;
  }

  return (
    <div className={cn("flex flex-col items-center", className)}>
      {/* Recording state + waveform */}
      {isRecording && (
        <div className="w-full mb-2 rounded-lg overflow-hidden bg-gradient-to-r from-[#0F1A1F] to-[#132920] px-3 py-2.5">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#FF9DA7]">
              Listening
            </span>
          </div>

          {/* Waveform canvas */}
          <canvas
            ref={canvasRef}
            width={280}
            height={36}
            className="w-full h-[36px] rounded"
          />

          {/* Interim transcript */}
          {interimTranscript && (
            <div className="mt-1.5 text-[12px] text-white/80 italic truncate">
              "{interimTranscript}"
            </div>
          )}
        </div>
      )}

      {/* Mic button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleRecording}
              className={cn(
                "w-8 h-8 rounded-full shrink-0",
                isRecording
                  ? "bg-red4 text-red2 hover:bg-red-200"
                  : "bg-gray7 text-gray3 hover:bg-gray6 hover:text-gray2"
              )}
            >
              {isRecording ? (
                <Square className="h-3.5 w-3.5" />
              ) : (
                <Mic className="h-3.5 w-3.5" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            {isRecording ? "Stop recording" : "Voice input"}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
